/**
 * AlertTimelineItem
 * Thin adapter that maps AlertExpanded's ActivityLogEntry → generic TimelineItem.
 */
import React from 'react';
import {
  MdOutlineAddAlert,
  TbBellRinging,
  TbRefreshAlert,
  IoMdEye,
  MdDangerous,
} from '@/components/atoms/Icons';
import type { ActivityLogEntry } from '@/pages/Alert/alerts.interface';
import { NoDataMessages } from '@/services/alert/alerts.constants';
import { getDateTime } from '@/utils/dateTime';
import { TimelineItem } from '@/components/elements/TimelineItem';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { APP_HEX } from '@/constant/theme/colors.constant';

const STATUS_DOT_BG: Record<string, string> = {
  green: APP_HEX.success,
  red: APP_HEX.danger,
  amber: APP_HEX.warning,
  gray: APP_HEX.muted,
};

const STATUS_CODE_COLOR: Record<number, string> = {
  200: 'green',
  201: 'green',
  422: 'red',
  500: 'red',
  0: 'gray',
  100: 'green',
};

function actionIncludesWord(action: string, word: string) {
  return action.toUpperCase().includes(word.toUpperCase());
}

export const AlertTimelineItem = ({ event }: { event: ActivityLogEntry }) => {
  const { action, message, activityDtm, statusCode, server, details } = event;
  const { status: dStatus, data: dData } = details || {};

  const dotColor =
    STATUS_DOT_BG[STATUS_CODE_COLOR[statusCode as number] ?? 'gray'];

  const icon = actionIncludesWord(action, 'CREATE') ? (
    <MdOutlineAddAlert className="h-3 w-3 text-white" />
  ) : actionIncludesWord(action, 'UPDATE') ||
    actionIncludesWord(action, 'PATCH') ? (
    <TbRefreshAlert className="h-3 w-3 text-white" />
  ) : actionIncludesWord(action, 'GET') ? (
    <IoMdEye className="h-3 w-3 text-white" />
  ) : actionIncludesWord(action, 'DELETE') ||
    actionIncludesWord(action, 'CLOSE') ? (
    <MdDangerous className="h-3 w-3 text-white" />
  ) : (
    <TbBellRinging className="h-3 w-3 text-white" />
  );

  const tooltip =
    [
      dStatus &&
        `${ORGANISM_PROPS.ALERT_TIMELINE_ITEM.TOOLTIP_STATUS_PREFIX}${dStatus}`,
      dData?.message &&
        `${ORGANISM_PROPS.ALERT_TIMELINE_ITEM.TOOLTIP_MESSAGE_PREFIX}${dData.message}`,
    ]
      .filter(Boolean)
      .join(ORGANISM_PROPS.ALERT_TIMELINE_ITEM.TOOLTIP_SEPARATOR) ||
    ORGANISM_PROPS.ALERT_TIMELINE_ITEM.TOOLTIP_NO_DETAILS;

  const subtitle = [message, server && `— ${server.toUpperCase()}`]
    .filter(Boolean)
    .join(' ');
  const meta = `${activityDtm ? getDateTime(activityDtm) : NoDataMessages.N_A_DATE}${ORGANISM_PROPS.ALERT_TIMELINE_ITEM.META_STATUS_PREFIX}${statusCode}`;

  return (
    <TimelineItem
      dotColor={dotColor}
      icon={icon}
      title={action}
      subtitle={subtitle}
      meta={meta}
      tooltip={tooltip}
    />
  );
};
